import React, { useMemo, useState } from 'react';
import { FaCheckCircle, FaCreditCard, FaLink, FaLock } from 'react-icons/fa';
import { SHARE_MODE_OPTIONS } from '../../services/giftBuilderConfig';

const SHARE_MODE_META = {
  private: {
    tag: 'Off',
    icon: FaLock,
  },
  link: {
    tag: 'Link',
    icon: FaLink,
  },
  moments: {
    tag: 'Card',
    icon: FaCreditCard,
  },
};

const SharingPrivacyStep = ({ pageData, theme, onSharingModeChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const currentMode = pageData.sharingSettings?.shareMode || 'link';
  const selectedOption = useMemo(
    () => SHARE_MODE_OPTIONS.find((option) => option.value === currentMode) || SHARE_MODE_OPTIONS[0],
    [currentMode]
  );
  const selectedMeta = SHARE_MODE_META[selectedOption.value] || SHARE_MODE_META.link;
  const SelectedIcon = selectedMeta.icon;

  const handleModeChange = (mode) => {
    onSharingModeChange(mode);
    setIsExpanded(false);
  };

  return (
    <section id="gift-builder-sharing" className="scroll-mt-6 lumirae-editor-panel p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.28em]" style={{ color: theme.accent }}>
            Step 4 / Share
          </div>
          <p className="mt-2 text-xs font-medium leading-relaxed opacity-60">
            Current setting: {selectedOption.label}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setIsExpanded((current) => !current)}
          className="lumirae-ds-button lumirae-ds-button-secondary px-4 py-3"
          aria-expanded={isExpanded}
        >
          {isExpanded ? 'Hide Options' : 'Change'}
        </button>
      </div>

      <div
        className="lumirae-ds-list-card mt-4 p-4"
        style={{
          borderColor: 'var(--lumirae-accent-primary)',
          boxShadow: '0 14px 34px rgba(33, 27, 29, 0.08)',
        }}
      >
        <span className="flex items-start gap-3">
          <span
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm shadow-sm"
            style={{
              backgroundColor: 'var(--lumirae-accent-soft)',
              color: theme.accent,
            }}
          >
            <SelectedIcon />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[9px] font-black uppercase tracking-[0.2em] opacity-50">
              {selectedMeta.tag}
            </span>
            <span className="mt-1 block text-xs font-black uppercase tracking-widest" style={{ color: theme.accent }}>
              {selectedOption.label}
            </span>
            <span className="mt-2 block text-xs font-medium leading-relaxed opacity-65">
              {selectedOption.description}
            </span>
          </span>
          <span
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-full px-3 py-2 text-[8px] font-black uppercase tracking-widest"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--lumirae-state-success) 12%, white)',
              color: 'var(--lumirae-state-success)',
            }}
          >
            <FaCheckCircle />
            Selected
          </span>
        </span>
      </div>

      {isExpanded && (
        <div className="mt-4 grid grid-cols-1 gap-3">
          {SHARE_MODE_OPTIONS.map((option) => {
            const active = currentMode === option.value;
            const meta = SHARE_MODE_META[option.value] || SHARE_MODE_META.link;
            const Icon = meta.icon;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => handleModeChange(option.value)}
                className={`lumirae-ds-list-card w-full p-4 text-left transition-all ${active ? 'is-active' : ''}`}
                style={{
                  borderColor: active ? 'var(--lumirae-accent-primary)' : 'var(--lumirae-border-default)',
                  boxShadow: active ? '0 14px 34px rgba(33, 27, 29, 0.09)' : 'none',
                }}
              >
                <span className="flex items-start gap-3">
                  <span
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm shadow-sm"
                    style={{
                      backgroundColor: active ? 'var(--lumirae-accent-soft)' : 'var(--lumirae-surface-raised)',
                      color: active ? theme.accent : 'var(--lumirae-text-secondary)',
                    }}
                  >
                    <Icon />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[9px] font-black uppercase tracking-[0.2em] opacity-50">
                      {meta.tag}
                    </span>
                    <span className="mt-1 block text-xs font-black uppercase tracking-widest" style={{ color: active ? theme.accent : theme.text }}>
                      {option.label}
                    </span>
                    <span className="mt-2 block text-xs font-medium leading-relaxed opacity-65">
                      {option.description}
                    </span>
                  </span>
                </span>
              </button>
            );
          })}
          <p className="text-[9px] font-black uppercase leading-relaxed tracking-widest opacity-45">
            Confirm before sealing: Link Access means anyone with the URL can open the gift. Share Card Only and Sharing Off remove share buttons, but they do not make an already shared link password-protected.
          </p>
        </div>
      )}
    </section>
  );
};

export default SharingPrivacyStep;
